import * as React from 'react';
import { Segment , Message, DropdownProps } from 'semantic-ui-react';
import { inject, observer } from 'mobx-react';

import Chart                    from 'src/stores/application/classes/Chart';
import UnitSensor               from 'src/stores/application/classes/UnitSensor';

import { 
    ChartType
} from 'src/services/graphql/Models';

import { SharedChartRenderProps, ComputedChartRenderProps } from './renders/Shared';

import { 
    Table,
    Bar,
    Donut,
    Line,
    Pie,
    PolarArea,
    Radar
} from './renders';
import './ChartView.scss';
import TriggerableStat from 'src/stores/application/classes/TriggerableStat';
import LoadingFrame from '../../components/LoadingFrame';
import GeoSpreadChart from 'src/container/Chart/renders/GeoSpreadChart';
import { SortedUnitSensor } from 'src/shared/SortedUnitSensorData';
import { observable, action, observe } from 'mobx';
import { ErroredPromise, CheckStores } from 'src/shared/utility';

interface ActiveInfoWrapper {
    [index: string]: Boolean;
}

export interface ChartViewStateInterface {
    height: number;
    width: number;
    // renderProps: ComputedChartRenderProps;
    // updater?: any;
    activeInfoWrapper: ActiveInfoWrapper;
}

interface ChartViewProps extends DropdownProps {
    [key: string]: any;
    chart: Chart;
    geospread?: Boolean;
    lockDetails?: Boolean;

    override?: {
        unitSensors: UnitSensor[];
        viewstat: TriggerableStat;
        fromDate: Date;
        toDate: Date;
    };
}

@inject(
    'commcaches',
    'unitsensors',
    'sensortypes',
    'triggerableStats',
    'units'
)
@observer
export default class ChartView extends React.Component<ChartViewProps, ChartViewStateInterface> {
    knownItems: any[];
    sortedUnitSensors: SortedUnitSensor[] = [];   
    @observable chartRenderProps: any;
    @observable lastUpdate: any = undefined;

    lastRenderTimestamp = Date.now();
    plainChartRenderProps: any;
    _unmounted: boolean = true;

    protected containingDiv: any;
    protected redraw: boolean;
    protected chartRef: any;
    protected duration: number;

    protected currentOverride: any = '';
    protected currentChart: any = '';

    _chartRenderProps(props?: any)  {
        props = props || this.props;
        const {
            commcaches,
            unitsensors,
            sensortypes,
            triggerableStats,
            units
        } = props;

        let err = CheckStores(
            props, 
            [
                'commcaches',
                'unitsensors',
                'sensortypes',
                'triggerableStats',
                'units'
            ]
        );
        if (err) {
            this._callForceUpdate('rebound');
            return ErroredPromise(err);
        }
        
        const chart = new Chart({
            ...this.props.chart,
            geospread: this.props.geospread
        });
        const override = this.props.override;
        const commCacheItems = commcaches.getParsedItems;
        
        return chart.getChartDataResult({
            commcaches, 
            unitsensors, 
            units,
            sensortypes,
            triggerableStats,
            override,
            commCacheItems
        });    
    }

    renderProps(_props?: any) {
        const state = this.state;
        const props = _props || this.props;
        
        if (
            typeof state === 'undefined' ||
            typeof props === 'undefined' ||
            typeof props.chart === 'undefined'
        ) {
            return undefined;
        }
        
        const dataResult = this.plainChartRenderProps;
        if (
            !dataResult
        ) {
            return undefined;
        }

        const data = dataResult.data;
        this.sortedUnitSensors = dataResult.sortedUnitSensors;

        return {
            CurrentChart: props.chart,
            data,
            redraw: this.redraw
        } as SharedChartRenderProps;
    }

    constructor (widgetData: ChartViewProps) {
        super(widgetData);
        this.redraw = false;

        this.state = {
            height: 0,
            width: 0,
            // renderProps: this.renderProps,
            activeInfoWrapper: {}
        };

        const callForceUpdate = this._callForceUpdate.bind(this);

        const { 
            units,
            unitsensors,
            commcaches,
            triggerableStats
        } = this.props;

        observe(units, 'getParsedItems', () => callForceUpdate('units'));
        observe(unitsensors, 'getParsedItems', () => callForceUpdate('unitsensors'));
        observe(commcaches, 'getParsedItems', (change) => callForceUpdate('commcaches'));
        observe(triggerableStats, 'getParsedItems', () => callForceUpdate('triggerableStats'));
        this.lastRenderTimestamp = Date.now();
    }
    
    @action
    updateRenderProps(me: any, props?: any) {
        
        if (!me._unmounted) {
            me._chartRenderProps.apply(me, props).then(newChartRenderProps  => {
                // console.log(`in update for ${this.props.chart.name}`);
                if (newChartRenderProps) {
                    me.plainChartRenderProps = newChartRenderProps;
                    me.chartRenderProps  = newChartRenderProps;
                }
            }).catch(console.log);
        }
    }

    public parseRenderProps (renderProps?: ComputedChartRenderProps) {
        const mapdata = (data: any) => data ? ({
            labels: data.labels,
            datasets: Array.isArray(data.datasets) ?
                data.datasets.map(set => ({
                    ...set,
                    _meta: undefined
                }))
                : undefined
        }) : undefined;

        return renderProps ? {
            height: renderProps.height,
            width: renderProps.width,
            redraw: renderProps.redraw,
            CurrentChart: renderProps.CurrentChart,
            data: Array.isArray(renderProps.data) ? 
            renderProps.data.map(mapdata)
            : mapdata(renderProps.data)
        } : undefined;
        
    }

    _callForceUpdate(source: any, props?: any, lastCheck?: number) { 
        const checkSpan = 500;
        const newUpdate = Date.now();
        if (typeof lastCheck === 'undefined') {
            this.lastUpdate = newUpdate;
            setTimeout(
                this._callForceUpdate.bind(this),
                checkSpan,
                source,
                props,
                newUpdate
            );
        } else if (
            this.lastUpdate === lastCheck
        ) { 
            this.updateRenderProps(this, props);
            // console.debug(`${source}: ${newUpdate}`);
        } else {
            // console.log(`rejected source ${source}: ${newUpdate}`);

        }
    }

    /**
     * This section resizes the chart to fill up the widget
     */
    public resizeChart() {
        if (this.containingDiv !== undefined) {
            const style = window.getComputedStyle(this.containingDiv);
            let height = Number(style.getPropertyValue('height').replace('px', '')) 
                - Number(style.getPropertyValue('padding-top').replace('px', '')) 
                - Number(style.getPropertyValue('padding-bottom').replace('px', ''));
            let width  = Number(style.getPropertyValue('width').replace('px', ''))
                - Number(style.getPropertyValue('padding-left').replace('px', '')) 
                - Number(style.getPropertyValue('padding-right').replace('px', ''));
        
            if (isNaN(height)) {
                height = this.containingDiv.clientHeight;
            }

            if (isNaN(width)) {
                width = this.containingDiv.clientWidth;
            }

            if (this.state.height !== height || this.state.width !== width) {
                this.setState({height: height, width: width});
            }
        }
    }
    
    onLegendClick(event: any, data: any) {
        if (this.props.onLegendClick) {
            let sensors = this.sortedUnitSensors;
            this.props.onLegendClick(sensors[data.datasetIndex]);
        }
        // this.sortedUnitSensors.map(o => );
    }

    public _renderChart(_renderProps: SharedChartRenderProps, override?: any) {
        const renderProps = {
            ..._renderProps,
            ...this.state,
            ...this.props,
            CurrentChart: this.props.chart,
            ...(override || {})
        };
        const legend = {onClick: this.onLegendClick.bind(this)};
        
        if (
            typeof renderProps === 'undefined'
        )  {
            return <LoadingFrame />;
        }
        if (this.props.chart.typedId) {
            switch (this.props.chart.chartType) {
                case ChartType.table:
                    return (
                        <Table 
                            ref={r => this.chartRef = r}
                            key={`table:${this.props.chart.typedId}`}
                            {...renderProps} 
                        />
                    );
                    
                case ChartType.bar:
                    return (
                        <Bar 
                            ref={r => this.chartRef = r}
                            key={`bar:${this.props.chart.typedId}`}
                            {...renderProps} 
                            legend={legend}
                        />
                    );
                    
                case ChartType.donut:
                    return (
                        <Donut
                            ref={r => this.chartRef = r}
                            key={`donut:${this.props.chart.typedId}`}
                            {...renderProps}
                            legend={legend}
                        />
                    );

                case ChartType.pie:
                    return (
                        <Pie
                            ref={r => this.chartRef = r}
                            key={`pie:${this.props.chart.typedId}`}
                            {...renderProps} 
                            legend={legend}
                        />
                    );

                case ChartType.polar:
                    return (
                        <PolarArea
                            ref={r => this.chartRef = r}
                            key={`polar:${this.props.chart.typedId}`}
                            {...renderProps}
                            legend={legend}
                        />
                    );

                case ChartType.radar:
                    return (
                        <Radar
                            ref={r => this.chartRef = r}
                            key={`radar:${this.props.chart.typedId}`}
                            {...renderProps}
                            legend={legend}
                        />
                    );

                case ChartType.line:
                    let unit;
                    if (this.duration < 10800000) {
                        unit = 'minute';
                    } else if (this.duration < 86400000) {
                        unit = 'hour';
                    } else if (this.duration < 1123200000) {
                        unit = 'day';
                    } else {
                        unit = 'week';
                    }
                    return (
                        <Line
                            ref={r => this.chartRef = r}
                            key={`line:${this.props.chart.typedId}`}
                            {...renderProps} 
                            unit={unit}
                            legend={legend}
                        />
                    );

                default:
                    return (
                        <div>
                            <Message
                                error
                                header="Unknown Chart Type"
                                list={[
                                    'This must be a new Chart'
                                ]}
                            />
                        </div>
                    );
            }
        } else {
            return (
                <LoadingFrame
                    type="bar"
                />
            );
        }
    }

    setMarker(name: string, active: Boolean) {
        this.setState({
            activeInfoWrapper: {
                ...this.state.activeInfoWrapper,
                [name]: active
            }
        });
    }

    public renderChart() {
        const _renderProps = this.chartRenderProps;
        const renderProps = this.plainChartRenderProps; // this.state.renderProps;
        
        if (
            typeof _renderProps !== 'undefined' &&
            typeof renderProps !== 'undefined'
        ) {
            if (this.props.geospread && Array.isArray(renderProps) ) {
                const geoSpread = renderProps.map(renderPropData => ({
                        position: renderPropData.unit.location, 
                        labelAnchor: [250, 0],
                        onClick: (
                            () => {
                                this.props.routing.push(`/unit/${renderPropData.unit.typedId}`);
                            }
                        ).bind(this),
                        onMouseOver: this.setMarker.bind(this, renderPropData.unit.typedId, true),
                        onMouseOut: this.setMarker.bind(this, renderPropData.unit.typedId, false),
                        // labelStyle: {backgroundColor: "yellow", fontSize: "32px", padding: "16px"}}
                        children: (
                            <div 
                                className={`geo-spread-chart ${
                                    this.state.activeInfoWrapper[renderPropData.unit.typedId] ||
                                    this.props.lockDetails ? 'active' : 'hidden'
                                }`}
                            >
                                {this._renderChart(
                                    renderPropData, 
                                    { 
                                        width: 500,
                                        height: 250 
                                    }
                                )}
                            </div>
                        )
                }));

                return (
                    // <Map 
                    //     google={(window as any).google}
                    //     initialCenter={{            
                    //         lat: -36.9448728,
                    //         lng: 174.6261753
                    //     }}
                    //     bounds={bounds}
                    //     // onTilesloaded={()=>this._fitTheBounds()}
                    // >
                    //     {(renderProps.data as any).map(renderPropData => (
                    //         <InfoWindow
                    //             key={`infoWindow-${renderPropData.unit.typedId}`}
                    //             visible
                    //             position={renderPropData.unit.location}
                    //         >
                    //             {console.log(renderPropData.unit)}
                    //             Testing the Data
                                
                    //         </InfoWindow>
                    //     ))}
                    // </Map>
                    <GeoSpreadChart geoSpread={geoSpread} />
                );
            } else {
                return this._renderChart(renderProps as SharedChartRenderProps);
            }
        } else {
            // console.log(`No chartrender for: ${this.props.chart.name}`);
            return (
                <div>
                    <LoadingFrame
                        type="grid"
                    />
                </div>
            );
        }
    }

    public render() {
        const newRenderTimestamp = Date.now();
        // const diff = newRenderTimestamp - this.lastRenderTimestamp;
        this.lastRenderTimestamp = newRenderTimestamp;
        // console.debug(`rendering ${diff} ${this.props.chart.typedId}`);
        return(
            <Segment 
                key={this.props.chart.typedId}
                className={`frame widget ${this.props.isDashboard ? 'dashboard-widget' : ''}`}
            >
                <div 
                    className={`widget-frame ${this.props.chart.chartType}-chart ${
                        this.props.geospread ? 'geo-spread' : ''
                    }`}
                    ref={thisDiv => this.containingDiv = thisDiv}
                >
                    {this.renderChart()}
                </div>
            </Segment>
        );
        
    }

    componentDidMount() {
        this._unmounted = false;
        this._callForceUpdate('mounted', this.props);
        // this.updateRenderProps();
        //  this.setState({ updater: setInterval(this.updateRenderProps.bind(this), 3500)});
    }

    componentWillUnmount() {
        this._unmounted = true;
        // window.clearInterval(this.state.updater);
    }

    componentWillReceiveProps(props: any) {
        this._callForceUpdate('changedProps', props);
    }
}

// export default GoogleApiWrapper(ChartView);



// WEBPACK FOOTER //
// ./src/container/Chart/ChartView.tsx